<script setup lang="ts">
import type { VDropdownItemProps } from "./VDropdownItem.types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useDropdownMenuItem } from "../Dropdown/dropdownMenu";
import VIcon from "../Icon/VIcon.vue";

defineOptions({
  name: "VDropdownItem",
});

const {
  closeOnClick = true,
  icon = null,
  flag = null,
  text,
  secondaryText = null,
  disabledNotice = undefined,
  disabled = false,
  selected = false,
  selection = undefined,
  hasPerms = true,
  size = "medium",
  hide,
} = defineProps<VDropdownItemProps>();

const emit = defineEmits<{
  (e: "click", event: MouseEvent): void
}>();

const isDisabled = computed(() => {
  return disabled || !hasPerms;
});

// In a dropdown that is a menu the item is a menu item, which the menu moves focus to with the arrow keys.
// Anywhere else, such as a dropdown with a search field or a list outside a dropdown, it is a button in the
// tab order.
const inMenu = useDropdownMenuItem();

const itemAttributes = computed(() => {
  if (!inMenu.value) {
    return {
      "role": "button",
      "tabindex": 0,
      "aria-pressed": selection ? selected : undefined,
      "aria-current": !selection && selected ? "true" as const : undefined,
    };
  }
  if (selection) {
    return {
      "role": selection === "single" ? "menuitemradio" : "menuitemcheckbox",
      "tabindex": -1,
      "aria-checked": selected,
    };
  }
  return {
    "role": "menuitem",
    "tabindex": -1,
    "aria-current": selected ? "true" as const : undefined,
  };
});

// A menu activates its items with Enter and Space; a button item does it itself.
function onKeydown(event: KeyboardEvent) {
  if (inMenu.value || (event.key !== "Enter" && event.key !== " ")) {
    return;
  }
  event.preventDefault();
  (event.currentTarget as HTMLElement).click();
}

function onClick(e: MouseEvent) {
  if (isDisabled.value) {
    e.preventDefault();
    e.stopPropagation();
    return;
  }

  if (closeOnClick && typeof hide === "function") {
    hide();
  }

  emit("click", e);
}

const { t } = useI18n();

const themeTooltip = computed(() => {
  if (disabled && disabledNotice && hasPerms) {
    return "verbleif-tooltip-gray";
  }
  return "verbleif-tooltip-ac";
});

const contentTooltip = computed(() => {
  if (disabled && disabledNotice && hasPerms) {
    return disabledNotice;
  }
  return t("permissions.ac.dropdown_item");
});

const disableTooltip = computed(() => {
  if (hasPerms && disabledNotice && disabled) {
    return false;
  }
  return hasPerms;
});
</script>

<template>
  <VTooltip
    :content="contentTooltip"
    placement="top"
    :theme="themeTooltip"
    :disabled="disableTooltip"
  >
    <template #reference>
      <div
        class="option"
        :class="[$attrs.class, { 'is-disabled': isDisabled, 'is-selected': selected, 'is-small': size === 'small' }]"
        v-bind="itemAttributes"
        data-menu-item
        :aria-disabled="isDisabled || undefined"
        @click="onClick"
        @keydown="onKeydown"
      >
        <div
          v-if="icon || $slots.icon"
          class="icon"
          aria-hidden="true"
        >
          <slot name="icon">
            <VIcon
              v-if="icon"
              :name="icon"
            />
          </slot>
        </div>
        <div
          v-if="flag"
          class="flag"
          aria-hidden="true"
        >
          <slot name="flag">
            <img
              :src="`/flags/${flag.toUpperCase()}.svg`"
              alt=""
            >
          </slot>
        </div>
        <span class="text">
          <span v-if="secondaryText" class="block truncate text-xs leading-4 text-gray-500" data-test="dropdown-item-secondary">{{ secondaryText }}</span>
          {{ text }}
        </span>
        <div v-if="$slots.append" class="append">
          <slot name="append" />
        </div>
        <VIcon
          v-if="selected"
          class="selected-check"
          name="circle-check"
          aria-hidden="true"
        />
      </div>
    </template>
  </VTooltip>
</template>

<style lang="scss" scoped>
@reference "../../tailwind-reference.css";
:deep(.option) {
  display: flex;
  align-items: center;
  transition: none;
  color: var(--text);
  user-select: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 5px;

  &:hover:not(.is-disabled),
  &:focus-visible {
    @apply bg-gray-100 dark:bg-dark-800/70;
  }
  &:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
  &:active:not(.is-disabled) {
    color: var(--primary-dark);
  }

  &.is-disabled {
    opacity: 0.25;
  }

  &.is-selected:not(.is-disabled) {
    @apply bg-gray-100 dark:bg-dark-800/70;
    color: var(--primary);

    .text {
      font-weight: 600;
    }
  }

  &.is-danger:not(.is-disabled) {
    color: theme("colors.danger.600");

    &:hover {
      color: theme("colors.danger.700");
    }
    &:active {
      color: theme("colors.danger.900");
    }
  }

  &.is-primary:not(.is-disabled) {
    color: var(--primary);

    &:hover {
      color: var(--primary-dark);
    }
    &:active {
      color: var(--primary-darker);
    }
  }

  &.is-warning:not(.is-disabled) {
    color: theme("colors.warning.600");

    &:hover {
      color: theme("colors.warning.700");
    }
    &:active {
      color: theme("colors.warning.900");
    }
  }

  .text {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: normal;
    line-height: 20px;
    text-align: left;
    transition: inherit;
  }

  .icon,
  .flag {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    height: 20px;
    margin-right: 10px;
    transition: inherit;
  }

  .icon {
    /* A 1.25em box around the 1em icon keeps the text of every item in one column. */
    width: 1.25em;
    font-size: 16px;
    line-height: 1;
  }

  /* The shared flag of VLocaleSelect and VLanguageSwitch: 18x12, 2px radius, thin edge. */
  .flag {
    width: 18px;

    img {
      display: block;
      width: 18px;
      height: 12px;
      object-fit: cover;
      border-radius: 2px;
      box-shadow: 0 0 0 1px rgb(0 0 0 / 8%);
    }
  }

  &.is-small {
    padding: 4px 8px;

    .icon {
      font-size: 13px;
      margin-right: 8px;
    }

    .flag {
      margin-right: 8px;
    }
  }

  .append {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    padding-left: 12px;
  }

  .selected-check {
    flex-shrink: 0;
    margin-left: 8px;
    color: var(--primary);
  }
}
</style>
